'use client';

/**
 * (community) error boundary (A1).
 *
 * Catches errors thrown while rendering a Community page. Renders inside the
 * community shell so the tape/header/footer persist and only the content area
 * shows this branded panel. Token-only, with retry, a link back to the forum,
 * and a contact route.
 */

import { LifeBuoy, RotateCw } from 'lucide-react';
import Link from 'next/link';
import { useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { Panel } from '@/components/ui/terminal';

export default function CommunityError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error('community error boundary', error);
  }, [error]);

  return (
    <main className="mx-auto max-w-[1600px] px-7 py-16">
      <Panel className="mx-auto max-w-lg p-10 text-center">
        <ErrorBull />
        <h1 className="mt-5 font-serif text-[26px] font-normal leading-tight text-[var(--color-text)]">
          This page hit a snag
        </h1>
        <p className="mx-auto mt-3 max-w-sm text-[14px] leading-relaxed text-[var(--color-text-muted)]">
          Something failed while loading this discussion. It&rsquo;s been logged. Try again, or head
          back to the community.
        </p>
        {error.digest ? (
          <p className="mt-4 font-mono text-[11px] tracking-[0.04em] text-[var(--color-faint)]">
            ref {error.digest}
          </p>
        ) : null}
        <div className="mt-7 flex flex-wrap justify-center gap-2.5">
          <Button type="button" variant="primary" size="sm" onClick={() => reset()}>
            <RotateCw size={15} aria-hidden="true" />
            Try again
          </Button>
          <Button asChild variant="secondary" size="sm">
            <Link href="/community">Back to community</Link>
          </Button>
        </div>
        <p className="mt-6 flex items-center justify-center gap-1.5 text-[12px] text-[var(--color-faint)]">
          <LifeBuoy size={13} aria-hidden="true" />
          Still stuck?{' '}
          <a
            href="mailto:support@stocksinglobal.com.au"
            className="text-[var(--color-steel)] hover:underline"
          >
            Contact support
          </a>
        </p>
      </Panel>
    </main>
  );
}

/** Bull mark — steel silhouette in a hairline ring; token-driven, decorative.
 *  Brand-neutral by intent: a bull reads as "markets", not as one house's
 *  logo, so it carries across both brands in `lib/brand/brands.ts`. If a
 *  brand-specific mark is ever wanted here, resolve it from BRAND rather than
 *  hardcoding a second silhouette. */
function ErrorBull() {
  return (
    <svg
      width="54"
      height="54"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
      className="mx-auto block"
    >
      <circle cx="24" cy="24" r="23" className="stroke-[var(--color-hair)]" strokeWidth="1.5" />
      <path
        d="M14 18c-2.6-1.2-4-3.2-4.4-5.6C11.8 13.6 14 15 16 16.6c1.2-.9 2.7-1.5 4.3-1.7 1.3-.15 2.5-.15 3.7 0 1.6.2 3.1.8 4.3 1.7 2-1.6 4.2-3 6.4-4.2-.4 2.4-1.8 4.4-4.4 5.6.7 1.2 1 2.6 1 4 0 4.6-3.7 8.3-8.6 8.3S14 26.6 14 22c0-1.4.3-2.8 1-4Z"
        className="stroke-[var(--color-steel)]"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="20.5" cy="22.5" r="1.1" className="fill-[var(--color-steel)]" />
      <circle cx="27.5" cy="22.5" r="1.1" className="fill-[var(--color-steel)]" />
    </svg>
  );
}
